import React, {useState} from 'react';
import { Button, DialogContent, TextField, Grid, Typography, makeStyles, Hidden, Divider, DialogActions} from "@material-ui/core";
import { ApplicationUser } from '../../../entities/ApplicationUser';
import {  ServerResponse } from '../../../services/server/OldWebClient';
import { AuthenticationService } from '../../../services/AuthenticationService';
import { useBuilderPreview } from '../../../utilities/useBuilderPreview';
import { ProfitWell } from '../../../utilities/Tracking/ProfitWell';
import { useLocation } from 'react-router-dom';
import { EventTracking, Location } from '../../../utilities/Tracking/EventTracking';
import { FieldValidationError, ValidationHelper } from '../../../services/server/ServerResponses';

const useStyles = makeStyles((theme) => ({
    root: {
        textAlign: 'center',
        paddingTop: theme.spacing(20),
    },
    signInContainer: {
        height: '100vh'
    },
    paperGridItem: {
        [theme.breakpoints.down('xs')]: {
            height: '100%'
        }
    },
    signInPaper: {
        padding: '0px 50px',
        maxWidth: 500,
        [theme.breakpoints.down('xs')]: {
            margin: '0px',
            padding: '0px 30px',
            paddingTop: '5%',
            height: '100%'
        }
    },
    logoImage: {
        maxWidth: "180px",
        margin: "30px 0px 20px 0px"
    },
    title: {
        fontSize: "1.7rem",
        fontWeight: 500
    },
    textButton: {
        color: "rgb(50, 153, 57, .8)",
        paddingLeft: '0px',
        paddingRight: '0px'
    },
    submitButton: {
        marginTop: 4,
        backgroundColor: "rgb(50, 153, 57, .9)",
        color: '#FFF',
        "&:hover": {
            backgroundColor: "rgb(50, 153, 57, .8)",
        },
    },
    buttonContainer: {
    },
    link: {
        color: "rgb(50, 153, 57, .8)",
        textDecoration: 'none'
    },
    messageLink: {
        color: "#00a5e7"
    },
    termsText: {
        opacity: .88,
    },
    cssLabel: {
        '&$cssFocused': {
            color: "rgb(50, 153, 57, .8)",
        },
        },
        cssFocused: {},
        cssUnderline: {
        '&:after': {
            borderBottomColor: "rgb(50, 153, 57, .8)",
            color: "rgb(50, 153, 57, .8)",
            },
        },
        cssOutlinedInput: {
        '&$cssFocused $notchedOutline': {
            borderColor: "rgb(50, 153, 57, .8)",
            color: "rgb(50, 153, 57, .8)",
            },
        },
        notchedOutline: {},
        divider: {
        borderLeft: "3px solid green",
        position: "absolute",
        left: "50%",
        marginLeft: "-1.5px",
    }
}));

export function SignInRegisterDialog() {
    const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);

    const toggleForgotPassword = () => {
        setIsDisabled(false);
        setShowForgotPassword(!showForgotPassword);
    }

    return (
        showForgotPassword
            ?
            <>
                <Typography variant="h4" style={{color: "rgb(50, 153, 57, .8)", padding: 24 }} >Forgot Your Password</Typography>
                <DialogContent>
                    <ForgotPasswordForm 
                        toggleForgotPassword={() => toggleForgotPassword()} 
                    />
                </DialogContent>
            </>
           :
            <>
                <Typography variant="h2" style={{color: "rgb(50, 153, 57, .8)", padding: 24}} >Sign in or register</Typography>
                <DialogContent>
                    <Hidden smDown>
                        <Grid container direction="row" wrap="nowrap" spacing={2}>
                            <Grid item>
                                <SignInForm 
                                    toggleForgotPassword={() => toggleForgotPassword()} 
                                    setIsDisabled={(disable:boolean) => setIsDisabled(disable)}
                                    isDisabled={isDisabled}/>
                            </Grid>
                            <Grid item style={{ marginTop: 32, display: 'flex'}}>
                                <hr />
                            </Grid>
                            <Grid item>
                                <RegisterForm 
                                    setIsDisabled={(disable:boolean) => setIsDisabled(disable)}
                                    isDisabled={isDisabled}/>
                            </Grid>
                        </Grid>
                    </Hidden>
                    <Hidden mdUp>
                        <Grid container direction="column">
                            <Grid item>
                                <SignInForm                         
                                    toggleForgotPassword={() => toggleForgotPassword()} 
                                    setIsDisabled={(disable:boolean) => setIsDisabled(disable)}
                                    isDisabled={isDisabled}/>
                            </Grid>
                            <Divider style={{ margin: "10px 0px"}}/>
                            <Grid item>
                                <RegisterForm 
                                    setIsDisabled={(disable:boolean) => setIsDisabled(disable)}
                                    isDisabled={isDisabled}/>
                            </Grid>
                        </Grid>
                    </Hidden>
                </DialogContent>
            </>
    );
}

interface SignInFormProps{
    toggleForgotPassword: () => void;
    isDisabled: boolean;
    setIsDisabled: (disable:boolean) => void;
}

function SignInForm(props: SignInFormProps){
    const [email, setEmail] = useState<string>('')
    const [password, setPassword] = useState<string>('')
    const [errors, setErrors] = useState<FieldValidationError[]>([])
    const { isDisabled } = props;
    const previewContext = useBuilderPreview();
    const classes = useStyles();

    const handleFormSubmit = async(e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault(); 
        if (isDisabled) {
            return;
        }
        
        props.setIsDisabled(true);
        const response = await AuthenticationService.signIn(email, password, false);
        props.setIsDisabled(false);

            
        if (ServerResponse.isSuccess<ApplicationUser>(response)) {
            EventTracking.Authentication.onLogin(Location.BuilderPreview)
            ProfitWell.onUserAuthenticated(response.data.email);
            previewContext.updateUser(response.data)
        }
        else if (ServerResponse.isModelValidation(response)) {
            if (response.valid) {
                const serverError: FieldValidationError = { field: "", errors: ['An unknown error occurred. Please try again.'] };
                setErrors([serverError]);
            }
            else {
                setErrors(response.errors);
            }
        }
        else {
            const serverError: FieldValidationError = { field: "", errors: [response.message] };
            setErrors([serverError]);
        }
    }

    return (
    <>
        <Typography variant="h6">Sign in to SermonCentral</Typography>
        <Grid container>
            <form onSubmit={handleFormSubmit}>
                <TextField
                    error={ValidationHelper.isFieldInError(errors, 'Email')}
                    required
                    autoFocus
                    fullWidth
                    id="email"
                    placeholder="Email address"
                    type="email"
                    margin="dense"
                    variant="outlined"
                    InputLabelProps={{
                        classes: {
                          root: classes.cssLabel,
                        },
                      }}
                      InputProps={{
                        classes: {
                          root: classes.cssOutlinedInput,
                          focused: classes.cssFocused,
                          notchedOutline: classes.notchedOutline,
                        },
                      }}
                    onChange={e => setEmail(e.target.value)}
                    disabled={isDisabled}
                    helperText={
                        ValidationHelper.isFieldInError(errors, "Email") &&
                        <span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Email').replace(/<(?:.|\n)*?>/gm, '') }} />}
                />
                <TextField
                    error={ValidationHelper.isFieldInError(errors, 'Password')}
                    required
                    fullWidth
                    id="password"
                    placeholder="Password(case-sensitive)"
                    type="password"
                    margin="dense"
                    variant="outlined"
                    InputLabelProps={{
                        classes: {
                          root: classes.cssLabel,
                        },
                      }}
                      InputProps={{
                        classes: {
                          root: classes.cssOutlinedInput,
                          focused: classes.cssFocused,
                          notchedOutline: classes.notchedOutline,
                        },
                      }}
                    
                    onChange={e => setPassword(e.target.value )}
                    disabled={isDisabled}
                    helperText={
                        ValidationHelper.isFieldInError(errors, "Password") &&
                        <span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Password') }} />}
                />
                <Button fullWidth variant="contained" size="medium" type="submit" className={classes.submitButton} disabled={isDisabled}>Sign In</Button>
            </form>
            <Grid container style={{marginTop: 10}} direction="row" justifyContent="space-between" alignItems="center">
                <Button 
                    variant="text" 
                    size="small" 
                    type="button" 
                    className={classes.textButton} 
                    disabled={isDisabled}
                    onClick={props.toggleForgotPassword}
                    >Forgot your password?</Button>
            </Grid>
        </Grid>
    </>
    )
}

interface RegisterFormProps {
    isDisabled: boolean;
    setIsDisabled: (disable:boolean) => void;
}

function RegisterForm(props: RegisterFormProps){

    const [fullName, setFullName] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [password, setPassword] = useState<string>('')
    const [errors, setErrors] = useState<FieldValidationError[]>([])
    const { isDisabled } = props;
    const previewContext = useBuilderPreview();
    const classes = useStyles();

    const registerUser = async(e:React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if(isDisabled){
            return;
        }

        let splitName = fullName.trim().split(' ');

        if (splitName.length < 2) {
            const serverError: FieldValidationError = { field: "FullName", errors: ['First and Last name are required'] };
            setErrors([serverError])
            return;
        }

        let lastName = splitName[splitName.length - 1].trim();
        let firstName = splitName.slice(0, splitName.length - 1).join(' ').trim();

        props.setIsDisabled(true);
        const response = await AuthenticationService.register({firstName, lastName, email, password});
        props.setIsDisabled(false);
            
        if (ServerResponse.isSuccess(response)) {
            EventTracking.Authentication.onNewRegistration(Location.BuilderPreview)
            previewContext.updateUser(response.data)
            return;
        }
        
        if (ServerResponse.isModelValidation(response)) {
            if (response.valid) {
                const serverError: FieldValidationError = { field: "", errors: ['An unknown error occurred. Please try again.'] };
                setErrors([serverError]);
            }
            else {
                setErrors(response.errors);
            }
        } else {
            const serverError: FieldValidationError = { field: "", errors: ['An unknown error occurred. Please try again.'] };
            setErrors([serverError]);
        }
    }

    return (
    <>
        <Typography variant="h6">Register a new account</Typography>
        <Grid container>
            <form onSubmit={registerUser}>
                <TextField
                    error={ValidationHelper.isFieldInError(errors, 'FullName')}
                    required
                    fullWidth
                    id="FullName"
                    placeholder="Full Name"
                    type="text"
                    margin="dense"
                    variant="outlined"
                    disabled={isDisabled}
                    InputLabelProps={{
                        classes: {
                          root: classes.cssLabel,
                        },
                      }}
                      InputProps={{
                        classes: {
                          root: classes.cssOutlinedInput,
                          focused: classes.cssFocused,
                          notchedOutline: classes.notchedOutline,
                        },
                      }}
                    onChange={e => setFullName(e.target.value)}
                    helperText={
                        ValidationHelper.isFieldInError(errors, "FullName") &&
                        <span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'FullName') }} />
                    }
                />
                <TextField
                    error={ValidationHelper.isFieldInError(errors, 'Email')}
                    required
                    autoFocus
                    fullWidth
                    id="Email"
                    type="Email"
                    placeholder="Email address"
                    margin="dense"
                    variant="outlined"
                    disabled={isDisabled}
                    InputLabelProps={{
                        classes: {
                          root: classes.cssLabel,
                        },
                      }}
                      InputProps={{
                        classes: {
                          root: classes.cssOutlinedInput,
                          focused: classes.cssFocused,
                          notchedOutline: classes.notchedOutline,
                        },
                      }}
                    onChange={e => setEmail(e.target.value)}
                    helperText={
                        ValidationHelper.isFieldInError(errors, "Email") &&
                        <span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Email').replace(/<(?:.|\n)*?>/gm, '') }} />
                    }
                />
                <TextField
                    error={ValidationHelper.isFieldInError(errors, 'Password')}
                    required
                    fullWidth
                    id="Password"
                    placeholder="Password(case-sensitive)"
                    type="password"
                    margin="dense"
                    variant="outlined"
                    disabled={isDisabled}
                    InputLabelProps={{
                        classes: {
                          root: classes.cssLabel,
                        },
                      }}
                      InputProps={{
                        classes: {
                          root: classes.cssOutlinedInput,
                          focused: classes.cssFocused,
                          notchedOutline: classes.notchedOutline,
                        },
                      }}
                    onChange={e => setPassword(e.target.value)}
                    helperText={
                        ValidationHelper.isFieldInError(errors, "Password") && 
                        <span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Password') }} />
                    }
                />
                <Button disabled={isDisabled} fullWidth variant="contained" size="medium" type="submit" className={classes.submitButton}>Register Free</Button>
            </form>
        </Grid>
    </>
    )
}


interface ForgotPasswordProps{
    toggleForgotPassword: () => void;
}

function ForgotPasswordForm(props: ForgotPasswordProps){

    const [email, setEmail] = useState<string>('')
    const [errors, setErrors] = useState<FieldValidationError[]>([])
    const classes = useStyles();
    const previewContext = useBuilderPreview();
    const loaction = useLocation();
    const [isDisabled, setIsDisabled] = useState<boolean>(false);

    const handleFormSubmit = async(e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (isDisabled) {
            return;
        }

        setIsDisabled(true)
        const response = await AuthenticationService.sendResetPasswordEmail(email, loaction.pathname);
        setIsDisabled(false)

        if (ServerResponse.isSuccess(response)) {
            props.toggleForgotPassword()
            previewContext.showSnackBar(
                "Reset Password Link Sent"
            )
        }
        else if (ServerResponse.isError(response)) {
            const serverError: FieldValidationError = { field: "Email", errors: [response.message] };
            setErrors([serverError]);
        }
        else {
            const serverError: FieldValidationError = { field: "Email", errors: [response.errors[0].errors[0]] };
            setErrors([serverError]);
        }
    }

    return (
        <>
            <Grid container direction="column" justifyContent="center" alignItems="center">
                <Typography variant="subtitle1">Enter your email address and we will send you an email with a link to reset your password.</Typography>
            </Grid>
            <form onSubmit={e => handleFormSubmit(e)}>
                <TextField
                    error={ValidationHelper.isFieldInError(errors, 'Email')}
                    required
                    autoFocus
                    fullWidth
                    id="email"
                    label="Email"
                    type="email"
                    margin="normal"
                    onChange={e => setEmail(e.target.value)}
                    helperText={<span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Email') }} />}
                />
                <Typography variant="body2" className={classes.termsText}>*Sermon Maker is part of SermonCentral.com. Use your SermonCentral.com account details to login.</Typography>    
                <DialogActions>
                    <Button onClick={props.toggleForgotPassword} variant="text" size="medium" color="primary" disabled={isDisabled} type="button" className={classes.textButton}>Cancel</Button>
                    <Button variant="contained" size="medium" color="primary" type="submit" disabled={isDisabled} className={classes.submitButton}>Send Email</Button>
                </DialogActions>
            </form>
        </>
    );
}